"use client";

/**
 * useSuppressVectorOverlay — page-level toggle for the global contrast vector
 * overlay (ContrastVectorMount).
 *
 * Pass `true` and the overlay hides for the lifetime of the calling component;
 * pass `false` (or unmount the component) and it reappears. The mount uses a
 * reference counter behind the scenes so multiple pages stacking the same
 * suppress signal don't end up cancelling each other on partial unmount.
 *
 * Used on detail pages whose own large artwork would otherwise compete with
 * the overlay strokes — track / release covers, artist heroes etc.
 */

import { useEffect, useRef } from "react";

const EVENT = "bb:vector-suppress";

export function useSuppressVectorOverlay(suppress: boolean): void {
  // Track whether WE currently hold a suppress claim so the cleanup releases
  // exactly the slot we acquired (idempotent across re-renders).
  const heldRef = useRef(false);

  useEffect(() => {
    if (suppress && !heldRef.current) {
      window.dispatchEvent(new CustomEvent(EVENT, { detail: { suppress: true } }));
      heldRef.current = true;
    } else if (!suppress && heldRef.current) {
      window.dispatchEvent(new CustomEvent(EVENT, { detail: { suppress: false } }));
      heldRef.current = false;
    }
  }, [suppress]);

  // Release on unmount in case the parent toggles state then unmounts in the
  // same flush — without this the counter could leak permanently up by one.
  useEffect(() => {
    return () => {
      if (heldRef.current) {
        window.dispatchEvent(new CustomEvent(EVENT, { detail: { suppress: false } }));
        heldRef.current = false;
      }
    };
  }, []);
}
